import {
  IDocumentCategory,
  IEnableDocumentCategory,
  IMoveAndDeleteDocumentCategory,
  ISystemAdminDocumentCategoryForCustomer,
} from 'src/generated-types'
import { apiConsts } from './apiConsts'
import { IPaginatedResult } from './paginated-result'
import { useDelete, useGet, usePost, usePut } from './reactQuery'
import { queryClient } from 'src/query-client'
import { CustomerDocumentCategoryKeys } from 'src/query-keys'

const documentCustomerAdminBaseURL = (customerId: EntityId) => `customers/${customerId}/documentCategories`

export const customerDocumentCategoryEndpoints = {
  getCustomerDocumentCategories(customerId: EntityId) {
    return `${documentCustomerAdminBaseURL(customerId)}`
  },
  getCustomerAndAdminDocumentCategories(customerId: EntityId) {
    return `${documentCustomerAdminBaseURL(customerId)}/getCustomerAndAdminDocumentCategories`
  },
  getSystemAdminDocumentCategoriesForCustomer(customerId: EntityId) {
    return `${documentCustomerAdminBaseURL(customerId)}/system`
  },
  getCustomerDocumentCategory(customerId: EntityId, id: EntityId) {
    return `${documentCustomerAdminBaseURL(customerId)}/${id}`
  },
  createCustomerDocumentCategory(customerId: EntityId) {
    return `${documentCustomerAdminBaseURL(customerId)}`
  },
  updateCustomerDocumentCategory(customerId: EntityId, id: EntityId) {
    return `${documentCustomerAdminBaseURL(customerId)}/${id}`
  },
  deleteCustomerDocumentCategory(customerId: EntityId) {
    return `${documentCustomerAdminBaseURL(customerId)}`
  },
  approveDocumentCategory(customerId: EntityId, id: EntityId) {
    return `${documentCustomerAdminBaseURL(customerId)}/${id}/switch-enable`
  },
  moveAndDeleteDocumentCategory(customerId: EntityId, id: EntityId) {
    return `${documentCustomerAdminBaseURL(customerId)}/${id}/move-and-delete`
  },
}

export const invalidateCustomerDocumentCategoryCache = {
  getCustomerDocumentCategories(customerId: EntityId) {
    queryClient.invalidateQueries(customerDocumentCategoryEndpoints.getCustomerDocumentCategories(customerId))
  },
  getCustomerAndAdminDocumentCategories(customerId?: EntityId) {
    queryClient.invalidateQueries(CustomerDocumentCategoryKeys.GET_CUSTOMER_AND_ADMIN_DOCUMENT_CATEGORIES(customerId))
  },
  getCustomerDocumentCategory(customerId: EntityId, id: EntityId) {
    queryClient.invalidateQueries(customerDocumentCategoryEndpoints.getCustomerDocumentCategory(customerId, id))
  },
  getSystemAdminDocumentCategoriesForCustomer(customerId: EntityId) {
    queryClient.invalidateQueries(
      customerDocumentCategoryEndpoints.getSystemAdminDocumentCategoriesForCustomer(customerId),
    )
  },
}

export const useGetCustomerDocumentCategories = (
  customerId: EntityId,
  page: number = apiConsts.Page,
  pageSize: number = apiConsts.Max,
) =>
  useGet<IPaginatedResult<IDocumentCategory>>(
    customerDocumentCategoryEndpoints.getCustomerDocumentCategories(customerId),
    { page, pageSize },
  )

export const useGetCustomerAndAdminDocumentCategories = (
  customerId: EntityId,
  page: number = apiConsts.Page,
  pageSize: number = apiConsts.Max,
  options?: { enabled?: boolean },
) =>
  useGet<IPaginatedResult<IDocumentCategory>>(
    customerDocumentCategoryEndpoints.getCustomerAndAdminDocumentCategories(customerId),
    { page, pageSize },
    {
      queryKey: CustomerDocumentCategoryKeys.GET_CUSTOMER_AND_ADMIN_DOCUMENT_CATEGORIES(customerId),
      enabled: options?.enabled,
    },
  )

export const useGetSystemAdminDocumentCategoriesForCustomer = (customerId: EntityId) =>
  useGet<ISystemAdminDocumentCategoryForCustomer>(
    customerDocumentCategoryEndpoints.getSystemAdminDocumentCategoriesForCustomer(customerId),
  )

export const useGetCustomerDocumentCategory = (customerId: EntityId, id: EntityId) =>
  useGet<IDocumentCategory>(customerDocumentCategoryEndpoints.getCustomerDocumentCategory(customerId, id))

export const useAddCustomerDocumentCategory = (customerId: EntityId) =>
  usePost<IDocumentCategory, IDocumentCategory>(
    customerDocumentCategoryEndpoints.createCustomerDocumentCategory(customerId),
  )

export const useUpdateCustomerDocumentCategory = (customerId: EntityId, id: EntityId) =>
  usePut<IDocumentCategory, IDocumentCategory>(
    customerDocumentCategoryEndpoints.updateCustomerDocumentCategory(customerId, id),
  )

export const useDeleteCustomerDocumentCategory = (customerId: EntityId) =>
  useDelete<IDocumentCategory>(customerDocumentCategoryEndpoints.deleteCustomerDocumentCategory(customerId))

export const useApproveDocumentCategory = (customerId: EntityId, id: EntityId) =>
  usePut<IEnableDocumentCategory, IEnableDocumentCategory>(
    customerDocumentCategoryEndpoints.approveDocumentCategory(customerId, id),
  )

export const useMoveAndDeleteDocumentCategory = (customerId: EntityId, id: EntityId) =>
  usePut<IMoveAndDeleteDocumentCategory, IMoveAndDeleteDocumentCategory>(
    customerDocumentCategoryEndpoints.moveAndDeleteDocumentCategory(customerId, id),
  )
